import { api, apiClient } from './client';
import { buildApiUrl } from './config';
import type { MatchingTestTask } from './chat';
import type {
  TaskDefinition,
  TaskDefinitionCreate,
  TaskDefinitionUpdate,
  TaskDefinitionListResponse,
  TaskInstance,
  TaskInstanceListResponse,
  TaskCompletion,
  TaskCompletionListResponse,
  CompleteTaskData,
  SnoozeOptions,
  TaskTypesResponse,
} from '@/types/task';

export interface TaskCompletionUpdate {
  notes?: string;
  completion_data?: Record<string, unknown>;
  completed_at?: string;
}

export const tasksApi = {
  /**
   * Get available task types.
   */
  async getTypes(): Promise<TaskTypesResponse> {
    return api.get('/api/tasks/types');
  },

  // Definitions

  /**
   * List task definitions for a tank.
   */
  async listDefinitions(
    tankId: string,
    params?: { status?: string; task_type?: string }
  ): Promise<TaskDefinitionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.task_type) searchParams.set('task_type', params.task_type);

    const query = searchParams.toString();
    const url = `/api/tanks/${tankId}/tasks/definitions${query ? `?${query}` : ''}`;
    return api.get(url);
  },

  /**
   * Get a single task definition.
   */
  async getDefinition(tankId: string, definitionId: string): Promise<TaskDefinition> {
    return api.get(`/api/tanks/${tankId}/tasks/definitions/${definitionId}`);
  },

  /**
   * Create a new task definition.
   */
  async createDefinition(tankId: string, data: TaskDefinitionCreate): Promise<TaskDefinition> {
    return api.post(`/api/tanks/${tankId}/tasks/definitions`, data);
  },

  /**
   * Update a task definition.
   */
  async updateDefinition(
    tankId: string,
    definitionId: string,
    data: TaskDefinitionUpdate
  ): Promise<TaskDefinition> {
    return api.put(`/api/tanks/${tankId}/tasks/definitions/${definitionId}`, data);
  },

  /**
   * Cancel a task definition (soft delete).
   * @deprecated Use archiveDefinition instead
   */
  async cancelDefinition(tankId: string, definitionId: string): Promise<void> {
    return api.delete(`/api/tanks/${tankId}/tasks/definitions/${definitionId}`);
  },

  /**
   * Pause a task definition (stop scheduling, keep available).
   */
  async pauseDefinition(tankId: string, definitionId: string): Promise<TaskDefinition> {
    return api.post(`/api/tanks/${tankId}/tasks/definitions/${definitionId}/pause`, {});
  },

  /**
   * Resume a paused task definition (restart scheduling).
   */
  async resumeDefinition(tankId: string, definitionId: string): Promise<TaskDefinition> {
    return api.post(`/api/tanks/${tankId}/tasks/definitions/${definitionId}/resume`, {});
  },

  /**
   * Archive a task definition (hide from view, keep history).
   */
  async archiveDefinition(tankId: string, definitionId: string): Promise<TaskDefinition> {
    return api.post(`/api/tanks/${tankId}/tasks/definitions/${definitionId}/archive`, {});
  },

  /**
   * Restore an archived task definition.
   */
  async restoreDefinition(tankId: string, definitionId: string): Promise<TaskDefinition> {
    return api.post(`/api/tanks/${tankId}/tasks/definitions/${definitionId}/restore`, {});
  },

  /**
   * Permanently delete a task definition and all history.
   */
  async deleteDefinition(tankId: string, definitionId: string): Promise<void> {
    return api.delete(`/api/tanks/${tankId}/tasks/definitions/${definitionId}?permanent=true`);
  },

  // Instances

  /**
   * List task instances for a tank.
   */
  async listInstances(tankId: string): Promise<TaskInstanceListResponse> {
    return api.get(`/api/tanks/${tankId}/tasks/instances`);
  },

  /**
   * Complete a task instance.
   */
  async completeInstance(
    tankId: string,
    instanceId: string,
    data: CompleteTaskData
  ): Promise<TaskCompletion> {
    return api.post(`/api/tanks/${tankId}/tasks/instances/${instanceId}/complete`, data);
  },

  /**
   * Snooze a task instance.
   */
  async snoozeInstance(
    tankId: string,
    instanceId: string,
    data: SnoozeOptions
  ): Promise<TaskInstance> {
    return api.post(`/api/tanks/${tankId}/tasks/instances/${instanceId}/snooze`, data);
  },

  /**
   * Skip a task instance.
   */
  async skipInstance(tankId: string, instanceId: string): Promise<void> {
    return api.post(`/api/tanks/${tankId}/tasks/instances/${instanceId}/skip`, {});
  },

  /**
   * Log a completion for a task definition (from All Tasks view).
   * If a pending instance exists, marks it complete and schedules next.
   */
  async logCompletion(
    tankId: string,
    definitionId: string,
    data: { completed_at?: string; notes?: string }
  ): Promise<TaskCompletion> {
    return api.post(
      `/api/tanks/${tankId}/tasks/definitions/${definitionId}/log-completion`,
      data
    );
  },

  // Completions

  /**
   * List task completions (history) for a tank.
   */
  async listCompletions(
    tankId: string,
    params?: {
      start_date?: string;
      end_date?: string;
      task_type?: string;
      definition_id?: string;
      limit?: number;
    }
  ): Promise<TaskCompletionListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.start_date) searchParams.set('start_date', params.start_date);
    if (params?.end_date) searchParams.set('end_date', params.end_date);
    if (params?.task_type) searchParams.set('task_type', params.task_type);
    if (params?.definition_id) searchParams.set('definition_id', params.definition_id);
    if (params?.limit) searchParams.set('limit', params.limit.toString());

    const query = searchParams.toString();
    const url = `/api/tanks/${tankId}/tasks/completions${query ? `?${query}` : ''}`;
    return api.get(url);
  },

  /**
   * Update a task completion.
   */
  async updateCompletion(
    tankId: string,
    completionId: string,
    data: TaskCompletionUpdate
  ): Promise<TaskCompletion> {
    return api.put(
      `/api/tanks/${tankId}/tasks/completions/${completionId}`,
      data
    );
  },

  /**
   * Delete a task completion.
   */
  async deleteCompletion(tankId: string, completionId: string): Promise<void> {
    await api.delete(`/api/tanks/${tankId}/tasks/completions/${completionId}`);
  },

  /**
   * Undo a task completion.
   * Reverts the instance to pending, removes auto-generated next instance
   * for recurring tasks, and deletes the completion record.
   */
  async undoCompletion(
    tankId: string,
    completionId: string
  ): Promise<{
    message: string;
    completion_id: string;
    instance_reverted: boolean;
    next_instance_deleted: boolean;
    dosing_event_deleted: boolean;
  }> {
    return api.post(
      `/api/tanks/${tankId}/tasks/completions/${completionId}/undo`,
      {}
    );
  },
};

/**
 * Fetch a matching testing task for a given parameter.
 *
 * Returns the matching pending test task instance if one exists,
 * or null if no match is found (HTTP 204).
 *
 * @param tankId - The tank ID to search tasks for
 * @param parameterKey - The water test parameter key (e.g., "alkalinity", "nitrate")
 * @returns MatchingTestTask if found, null otherwise
 */
export async function fetchMatchingTestTask(
  tankId: string,
  parameterKey: string
): Promise<MatchingTestTask | null> {
  const url = buildApiUrl(
    `/api/tanks/${tankId}/tasks/matching-test?parameter_key=${encodeURIComponent(parameterKey)}`
  );
  const response = await apiClient(url);
  if (response.status === 204) return null;
  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `HTTP ${response.status}`);
  }
  return response.json();
}
